/**
 * Meal Details Dialog Component
 *
 * Modal that displays detailed information about a meal including instructions,
 * ingredients, nutrition facts, and quick stats. Features responsive side panel
 * layout with comprehensive dark mode support and enhanced macronutrient visualizations.
 * Includes MealActionsMenu for editing operations when user has permission.
 *
 * @module components/meal-plans/MealDetailsDialog
 */

import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '@/hooks/useTheme';
import { useToast } from '@/hooks/useToast';
import { usePDFExport } from '@/hooks/usePDFExport';
import { useShoppingList } from '@/hooks/shopping-list/useShoppingList';
import { useUserProfileStore } from '@/stores/userProfileStore';
import { CloseButton } from '@/components/ui/CloseButton';
import { Toast } from '@/components/ui/Toast';
import { AddIngredientsModal } from '@/components/shopping-list/AddIngredientsModal';
import { MealActionsMenu } from './MealActionsMenu';
import { EditMealDialog } from './EditMealDialog';
import { AdjustMealDialog } from './AdjustMealDialog';
import { SwapMealDialog } from './SwapMealDialog';
import type { MealPlanFrontend, MealAction } from '@/types/mealPlan';

/**
 * Props for MealDetailsDialog component
 */
export interface MealDetailsDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Callback to close the dialog */
  onClose: () => void;
  /** The meal data to display */
  meal: MealPlanFrontend['mealPlanData']['meals'][0];
  /** Optional meal index for display */
  mealIndex?: number;
  /** ID of the meal plan containing this meal */
  mealPlanId?: string;
  /** ID of the meal plan creator (for permission checks) */
  creatorId?: string;
  /** ID of the current user (for permission checks) */
  currentUserId?: string;
}

const formatCurrency = (value: number): string => `$${value.toFixed(2)} AUD`;

const formatMealType = (type?: string): string | undefined => {
  if (!type) {
    return undefined;
  }

  return type
    .split('_')
    .map(segment => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
};

const cuisinePillStyle = {
  backgroundColor: '#f3f4f6',
  color: '#374151'
};

const mealTypePillStyles: Record<string, { backgroundColor: string; color: string }> = {
  breakfast: { backgroundColor: '#fef3c7', color: '#92400e' },
  lunch: { backgroundColor: '#d1fae5', color: '#065f46' },
  dinner: { backgroundColor: '#dbeafe', color: '#1e40af' },
  snack: { backgroundColor: '#e0e7ff', color: '#3730a3' },
  afternoon_tea: { backgroundColor: '#ffedd5', color: '#9a3412' },
  morning_tea: { backgroundColor: '#e0f2fe', color: '#075985' },
  dessert: { backgroundColor: '#ffe4e6', color: '#9d174d' }
};

const getMealTypePillStyle = (type?: string) => {
  if (!type) {
    return cuisinePillStyle;
  }
  return mealTypePillStyles[type] || { backgroundColor: '#ede9fe', color: '#5b21b6' };
};

const macroConfig = [
  { key: 'proteinG', label: 'Protein', max: 50, unit: 'g', color: '#f77a6e' },
  { key: 'carbsG', label: 'Carbs', max: 100, unit: 'g', color: '#3349c0' },
  { key: 'fatG', label: 'Fat', max: 30, unit: 'g', color: '#b5d98c' }
] as const;

const getMacroWidth = (value: number, max: number): string => {
  if (max <= 0) {
    return '0%';
  }
  return `${Math.min((value / max) * 100, 100)}%`;
};

interface IconProps {
  color?: string;
}

const CostIcon: React.FC<IconProps> = ({ color = '#059669' }) => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
  </svg>
);

const ServingsIcon: React.FC<IconProps> = ({ color = '#2563eb' }) => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
);

const TimeIcon: React.FC<IconProps> = ({ color = '#6b7280' }) => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const CookIcon: React.FC<IconProps> = ({ color = '#f97316' }) => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 9h18M5 9l1 10h12l1-10M8 5h8" />
  </svg>
);

type QuickStatKey = 'cost' | 'servings' | 'prep' | 'cook';

interface QuickStatConfig {
  key: QuickStatKey;
  label: string;
  value: string;
  icon: React.ReactNode;
  accentColor: string;
}

/**
 * Meal Details Dialog Component
 */
export const MealDetailsDialog: React.FC<MealDetailsDialogProps> = ({
  isOpen,
  onClose,
  meal,
  mealIndex,
  mealPlanId,
  creatorId,
  currentUserId
}) => {
  const { isDark } = useTheme();
  const profile = useUserProfileStore((state) => state.profile);
  const { toast, showSuccess, showError, closeToast } = useToast();
  const { exportMeal: exportMealPdf } = usePDFExport();
  const { shoppingList, addItems: addShoppingItems, mutating: shoppingListMutating } = useShoppingList();
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [activeDialog, setActiveDialog] = useState<MealAction | null>(null);
  const [addIngredientsOpen, setAddIngredientsOpen] = useState(false);
  const [addingIngredients, setAddingIngredients] = useState(false);
  const [pendingIngredients, setPendingIngredients] = useState<{ name: string; quantity: number; unit: string }[]>([]);
  const isPatient = profile?.role === 'patient';
  const palette = useMemo(() => ({
    overlay: isDark ? 'rgba(0, 0, 0, 0.85)' : 'rgba(15, 23, 42, 0.55)',
    container: isDark ? '#0f172a' : '#ffffff',
    surface: isDark ? '#111827' : '#f9fafb',
    contrastSurface: isDark ? '#1f2937' : '#ffffff',
    textPrimary: isDark ? '#f9fafb' : '#111827',
    textSecondary: isDark ? '#9ca3af' : '#6b7280',
    border: isDark ? '#1f2937' : '#e5e7eb',
    cardBorder: isDark ? '#1f2937' : '#e5e7eb',
    shadow: isDark ? '0 40px 80px rgba(0, 0, 0, 0.85)' : '0 40px 80px rgba(15, 23, 42, 0.2)'
  }), [isDark]);

  // Check for mobile/tablet screen size
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    // Check on mount
    checkScreenSize();

    // Add event listener for resize
    window.addEventListener('resize', checkScreenSize);

    // Cleanup
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Handle escape key press and body scroll
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when dialog is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Helper function to process instructions
  const processInstructions = (instructions?: string | string[] | null): string[] => {
    if (Array.isArray(instructions)) {
      return instructions.map(instruction =>
        instruction.replace(/^Step\s+\d+:\s*/i, '').trim()
      );
    }

    if (typeof instructions === 'string') {
      // Split by common delimiters and clean up
      return instructions
        .split(/\n|\.\s+(?=[A-Z])|;\s+/)
        .map(instruction => instruction.replace(/^Step\s+\d+:\s*/i, '').trim())
        .filter(instruction => instruction.length > 0);
    }

    return ['No instructions available'];
  };

  // Handle action selection from menu
  const handleActionSelect = (action: MealAction) => {
    setActiveDialog(action);
  };

  // Handle dialog close
  const handleDialogClose = () => {
    setActiveDialog(null);
  };

  // Handle successful operation
  const handleSuccess = () => {
    setActiveDialog(null);
    // Optionally refresh meal data or show success message
  };

  // Check if user has edit permission
  const canEdit = Boolean(creatorId && currentUserId && creatorId === currentUserId);

  if (!isOpen || !meal) {
    return null;
  }

  const processedInstructions = processInstructions(meal.instructions);
  const normalizedIngredients = (meal.ingredients ?? []).map((ingredient) => (
    typeof ingredient === 'string'
      ? { name: ingredient, amount: 0, unit: '', costAud: 0 }
      : ingredient
  ));
  const pillData = [
    meal.cuisine ? { label: meal.cuisine, style: cuisinePillStyle } : null,
    formatMealType(meal.mealType)
      ? { label: formatMealType(meal.mealType) as string, style: getMealTypePillStyle(meal.mealType) }
      : null
  ].filter(Boolean) as Array<{ label: string; style: { backgroundColor: string; color: string } }>;
  const prepTime = meal.prepTimeMinutes ?? 0;
  const cookTime = meal.cookTimeMinutes ?? 0;
  const servings = meal.servings ?? 0;
  const quickStats: QuickStatConfig[] = [
    {
      key: 'cost',
      label: 'Estimated cost',
      value: formatCurrency(meal.estimatedCostAud ?? 0),
      icon: <CostIcon color="#059669" />,
      accentColor: isDark ? 'rgba(6, 78, 59, 0.3)' : '#d1fae5'
    },
    {
      key: 'servings',
      label: 'Servings',
      value: `${servings} ${servings === 1 ? 'serving' : 'servings'}`,
      icon: <ServingsIcon color="#2563eb" />,
      accentColor: isDark ? 'rgba(30, 64, 175, 0.3)' : '#dbeafe'
    },
    {
      key: 'prep',
      label: 'Preparation time',
      value: `${prepTime} min`,
      icon: <TimeIcon color="#9333ea" />,
      accentColor: isDark ? 'rgba(91, 33, 182, 0.35)' : '#ede9fe'
    },
    {
      key: 'cook',
      label: 'Cooking time',
      value: `${cookTime} min`,
      icon: <CookIcon color="#f97316" />,
      accentColor: isDark ? 'rgba(154, 52, 18, 0.35)' : '#ffedd5'
    }
  ];
  const hasInstructions = !(processedInstructions.length === 1 && processedInstructions[0] === 'No instructions available');
  const cardStyle = {
    backgroundColor: palette.surface,
    borderRadius: '16px',
    border: `1px solid ${palette.cardBorder}`,
    padding: '20px'
  };
  const sectionTitleStyle = {
    fontSize: '16px',
    fontWeight: 600,
    color: palette.textPrimary,
    marginBottom: '16px'
  };
  const headerContainerStyle = {
    display: 'flex',
    flexDirection: 'row' as const,
    alignItems: isMobile ? 'flex-start' : 'center',
    justifyContent: 'space-between',
    gap: '16px',
    flexWrap: 'wrap' as const
  };
  const headerActionsStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap' as const,
    marginLeft: isMobile ? 'auto' : undefined,
    alignSelf: 'flex-start' as const
  };

  const handleExportMeal = async () => {
    try {
      await exportMealPdf(meal);
      showSuccess('PDF export started. Check your downloads shortly.');
    } catch (exportError) {
      console.error('Failed to export meal PDF from dialog:', exportError);
      showError('Unable to export the PDF right now. Please try again.');
    }
  };

  const buildMealIngredients = () =>
    (meal.ingredients ?? []).map((ingredient) => ({
      name: typeof ingredient === 'string' ? ingredient : ingredient.name,
      quantity: typeof ingredient === 'string' ? 0 : ingredient.amount ?? 0,
      unit: typeof ingredient === 'string' ? 'item' : (ingredient.unit || 'item'),
    }));

  const handleAddIngredientsConfirm = async (mode: 'append' | 'replace') => {
    if (!isPatient) {
      return;
    }
    const items = pendingIngredients.length ? pendingIngredients : buildMealIngredients();
    if (!items.length) {
      showError('No ingredients available for this meal.');
      setAddIngredientsOpen(false);
      return;
    }

    setAddingIngredients(true);
    try {
      await addShoppingItems({ mode, items });
      showSuccess('Meal ingredients added to shopping list');
      setAddIngredientsOpen(false);
      setPendingIngredients([]);
    } catch (err) {
      console.error('Failed to add meal ingredients:', err);
      showError('Unable to add meal ingredients right now. Please try again.');
    } finally {
      setAddingIngredients(false);
    }
  };

  const handleAddMealIngredients = async () => {
    if (!isPatient) {
      return;
    }
    const mealItems = buildMealIngredients();

    if (!mealItems.length) {
      showError('No ingredients available for this meal.');
      return;
    }

    const existingCount = shoppingList?.items?.length ?? 0;
    if (existingCount > 0) {
      setPendingIngredients(mealItems);
      setAddIngredientsOpen(true);
      return;
    }

    setAddingIngredients(true);
    try {
      await addShoppingItems({ mode: 'append', items: mealItems });
      showSuccess('Meal ingredients added to shopping list');
    } catch (err) {
      console.error('Failed to add meal ingredients:', err);
      showError('Unable to add meal ingredients right now. Please try again.');
    } finally {
      setAddingIngredients(false);
    }
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="meal-dialog-title"
      style={{
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: palette.overlay,
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        style={{
          backgroundColor: palette.container,
          borderRadius: '20px',
          width: '100%',
          maxWidth: '1150px',
          maxHeight: '94vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: palette.shadow,
          border: `1px solid ${palette.border}`,
          overflow: 'hidden'
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <div
          style={{
            padding: isMobile ? '24px' : '32px',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
            paddingBottom: isMobile ? '48px' : '64px'
          }}
        >
          {/* Header */}
          <div style={headerContainerStyle}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <h2
                  id="meal-dialog-title"
                  style={{
                    fontSize: isMobile ? '24px' : '30px',
                    fontWeight: 700,
                    color: palette.textPrimary,
                    margin: 0
                  }}
                >
                  {meal.mealName}
                </h2>
              </div>
              {pillData.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '12px' }}>
                  {pillData.map(({ label, style }) => (
                    <span
                      key={label}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '999px',
                        fontSize: '12px',
                        fontWeight: 600,
                        backgroundColor: style.backgroundColor,
                        color: style.color
                      }}
                    >
                      {label}
                    </span>
                  ))}
                  {typeof mealIndex === 'number' && (
                    <span style={{ padding: '6px 12px', fontSize: '14px', color: palette.textSecondary }}>
                      Meal #{mealIndex + 1}
                    </span>
                  )}
                </div>
              )}
            </div>
            <div style={headerActionsStyle}>
              <MealActionsMenu
                meal={meal}
                mealPlanId={mealPlanId ?? ''}
                onActionSelect={handleActionSelect}
                position="dialog"
                onExportMeal={handleExportMeal}
                showEdit={canEdit}
                showAdjust={canEdit}
                showSwap={canEdit}
                showExport
                showAddToShoppingList={!canEdit && isPatient}
                onAddToShoppingList={handleAddMealIngredients}
              />
              <CloseButton
                onClick={onClose}
                ariaLabel="Close dialog"
                className="ml-3"
              />
            </div>
          </div>

          {/* Quick Stats */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))',
              gap: '16px'
            }}
          >
            {quickStats.map(({ key, label, value, icon, accentColor }) => (
              <div
                key={key}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '16px',
                  borderRadius: '16px',
                  backgroundColor: palette.surface,
                  border: `1px solid ${palette.cardBorder}`
                }}
              >
                <div
                  style={{
                    width: '42px',
                    height: '42px',
                    borderRadius: '999px',
                    backgroundColor: accentColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {icon}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <span style={{ fontSize: '12px', color: palette.textSecondary }}>
                    {label}
                  </span>
                  <span style={{ fontSize: '16px', fontWeight: 600, color: palette.textPrimary }}>
                    {value}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Content */}
          <div
            style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: '24px',
              minHeight: '420px'
            }}
          >
            <div
              style={{
                width: isMobile ? '100%' : '40%',
                display: 'flex',
                flexDirection: 'column',
                gap: '24px'
              }}
            >
              <div style={cardStyle}>
                <div style={sectionTitleStyle}>Ingredients ({normalizedIngredients.length})</div>
                {normalizedIngredients.length > 0 ? (
                  <div>
                    {normalizedIngredients.map((ingredient, index) => {
                      const hasAmount = ingredient.amount > 0 && ingredient.unit;
                      return (
                        <div
                          key={`${ingredient.name}-${index}`}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '12px',
                            padding: '10px 0',
                            borderBottom: index < normalizedIngredients.length - 1 ? `1px solid ${palette.cardBorder}` : 'none'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
                            <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#3349c0' }} />
                            <span style={{ fontSize: '14px', color: palette.textPrimary, lineHeight: 1.5 }}>
                              {hasAmount ? `${ingredient.amount} ${ingredient.unit} ${ingredient.name}` : ingredient.name}
                            </span>
                          </div>
                          {ingredient.costAud > 0 && (
                            <span style={{ fontSize: '13px', color: palette.textSecondary, fontWeight: 500 }}>
                              {formatCurrency(ingredient.costAud)}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p style={{ margin: 0, color: palette.textSecondary, fontSize: '14px' }}>
                    No ingredients listed.
                  </p>
                )}
              </div>

              {meal.nutrition && (
                <div style={cardStyle}>
                  <div style={sectionTitleStyle}>Nutrition (Per Serving)</div>
                  <div
                    style={{
                      backgroundColor: palette.contrastSurface,
                      borderRadius: '12px',
                      padding: '16px',
                      textAlign: 'center',
                      border: `1px solid ${palette.cardBorder}`,
                      marginBottom: '16px'
                    }}
                  >
                    <div style={{ fontSize: '24px', fontWeight: 700, color: '#b58a48' }}>
                      {Math.round(meal.nutrition.caloriesPerServing)}
                    </div>
                    <div style={{ fontSize: '12px', letterSpacing: '1px', color: palette.textSecondary }}>
                      CALORIES
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {macroConfig.map(({ key, label, max, unit, color }) => {
                      const value = meal.nutrition?.[key];
                      if (typeof value !== 'number') {
                        return null;
                      }
                      return (
                        <div key={key}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <span style={{ fontSize: '13px', color: palette.textPrimary }}>{label}</span>
                            <span style={{ fontSize: '13px', color: palette.textSecondary }}>
                              {Math.round(value)} {unit}
                            </span>
                          </div>
                          <div
                            style={{
                              width: '100%',
                              height: '6px',
                              backgroundColor: palette.cardBorder,
                              borderRadius: '999px',
                              marginTop: '6px',
                              overflow: 'hidden'
                            }}
                          >
                            <div
                              style={{
                                width: getMacroWidth(value, max),
                                height: '100%',
                                backgroundColor: color,
                                borderRadius: '999px',
                                transition: 'width 0.3s ease'
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div style={{ flex: 1, marginBottom: isMobile ? '8px' : '12px' }}>
              <div style={{ ...cardStyle }}>
                <div style={sectionTitleStyle}>Instructions</div>
                {hasInstructions ? (
                  <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {processedInstructions.map((instruction, index) => (
                      <li key={`instruction-${index}`} style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                        <span
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '999px',
                            backgroundColor: '#3349c0',
                            color: '#ffffff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '13px',
                            fontWeight: 600,
                            flexShrink: 0
                          }}
                        >
                          {index + 1}
                        </span>
                        <span style={{ color: palette.textPrimary, fontSize: '14px', lineHeight: 1.6 }}>
                          {instruction}
                        </span>
                      </li>
                    ))}
                  </ol>
                ) : (
                  <p style={{ margin: 0, color: palette.textSecondary, fontSize: '14px' }}>
                    No instructions available.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Meal Dialog */}
      {activeDialog === 'edit' && mealPlanId && (
        <EditMealDialog
          isOpen={true}
          onClose={handleDialogClose}
          meal={meal}
          mealPlanId={mealPlanId}
          onSuccess={handleSuccess}
        />
      )}

      {/* Adjust Meal Dialog */}
      {activeDialog === 'adjust' && mealPlanId && (
        <AdjustMealDialog
          isOpen={true}
          onClose={handleDialogClose}
          meal={meal}
          mealPlanId={mealPlanId}
          onSuccess={handleSuccess}
        />
      )}

      {/* Swap Meal Dialog */}
      {activeDialog === 'swap' && mealPlanId && (
        <SwapMealDialog
          isOpen={true}
          onClose={handleDialogClose}
          meal={meal}
          mealPlanId={mealPlanId}
          onSuccess={handleSuccess}
        />
      )}

      {/* Add Ingredients Modal layered above dialog */}
      <AddIngredientsModal
        isOpen={addIngredientsOpen}
        onClose={() => {
          setAddIngredientsOpen(false);
          setPendingIngredients([]);
        }}
        onConfirm={handleAddIngredientsConfirm}
        mealCount={1}
        ingredientCount={pendingIngredients.length || buildMealIngredients().length}
        isSubmitting={addingIngredients || shoppingListMutating}
      />

      {/* Toast shown above dialog */}
      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={closeToast}
        duration={3000}
      />
    </div>,
    document.body
  );
};

export default MealDetailsDialog;